/**
 * City Form Drawer
 * Create/Edit form for cities with country selection
 */

import { useEffect } from 'react'
import { useTranslation, useForm, Controller, zodResolver, z } from '@ufc/core'
import type { LocalizedText } from '@ufc/core'
import {
  BaseDrawer,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  cn,
  Building2,
  Save,
  X,
} from '@ufc/uikit'
import type { City, CountrySummary } from '../../domain/types'
import { T_KEYS } from '../../domain/constants'

// ============================================================================
// Types
// ============================================================================

export interface CityFormDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  city?: City | null
  countries: CountrySummary[]
  onSubmit: (data: CityFormSubmitData) => void
  isLoading?: boolean
  isRTL?: boolean
}

export interface CityFormSubmitData {
  code: string
  name: LocalizedText
  stateProvince?: LocalizedText
  salesDistrict?: string
  isActive: boolean
  regionId?: string
  countryId?: string
}

// ============================================================================
// Schema & Types
// ============================================================================

interface CityFormData {
  code: string
  nameEn: string
  nameAr: string
  stateProvinceEn: string
  stateProvinceAr: string
  salesDistrict: string
  isActive: boolean
  countryId: string
}

const cityFormSchema = z.object({
  code: z
    .string()
    .min(1, T_KEYS.CITIES.VALIDATION.CODE_REQUIRED)
    .max(50, 'Code must be 50 characters or less'),
  nameEn: z.string().min(1, T_KEYS.CITIES.VALIDATION.NAME_REQUIRED),
  nameAr: z.string().default(''),
  stateProvinceEn: z.string().default(''),
  stateProvinceAr: z.string().default(''),
  salesDistrict: z.string().max(50).default(''),
  isActive: z.boolean().default(true),
  countryId: z.string().default(''),
})

// ============================================================================
// Utility Functions
// ============================================================================

function generateCityCode(): string {
  const timestamp = Date.now().toString(36).toUpperCase()
  const random = Math.random().toString(36).substring(2, 4).toUpperCase()
  return `CITY-${timestamp}-${random}`
}

const NO_SELECTION_VALUE = '__none__'

function buildLocalizedName(en: string, ar: string): LocalizedText {
  const trimmedEn = en.trim()
  const trimmedAr = ar.trim()
  const out: LocalizedText = { en: trimmedEn }
  if (trimmedAr) {
    out.ar = trimmedAr
  }
  return out
}

function buildLocalizedStateProvince(en: string, ar: string): LocalizedText | undefined {
  const trimmedEn = en.trim()
  const trimmedAr = ar.trim()
  if (!trimmedEn && !trimmedAr) return undefined
  const out: LocalizedText = { en: trimmedEn }
  if (trimmedAr) {
    out.ar = trimmedAr
  }
  return out
}

function getDefaultValues(city?: City | null): CityFormData {
  if (city) {
    return {
      code: city.code,
      nameEn: city.name.en,
      nameAr: city.name.ar ?? '',
      stateProvinceEn: city.stateProvince?.en ?? '',
      stateProvinceAr: city.stateProvince?.ar ?? '',
      salesDistrict: city.salesDistrict ?? '',
      isActive: city.isActive,
      countryId: city.country?.documentId || NO_SELECTION_VALUE,
    }
  }
  return {
    code: generateCityCode(),
    nameEn: '',
    nameAr: '',
    stateProvinceEn: '',
    stateProvinceAr: '',
    salesDistrict: '',
    isActive: true,
    countryId: NO_SELECTION_VALUE,
  }
}

function formDataToSubmitData(data: CityFormData): CityFormSubmitData {
  const countryId = data.countryId === NO_SELECTION_VALUE ? undefined : data.countryId

  return {
    code: data.code.trim().toUpperCase(),
    name: buildLocalizedName(data.nameEn, data.nameAr),
    stateProvince: buildLocalizedStateProvince(data.stateProvinceEn, data.stateProvinceAr),
    salesDistrict: data.salesDistrict.trim() || undefined,
    isActive: data.isActive,
    countryId,
  }
}

// ============================================================================
// Component
// ============================================================================

export function CityFormDrawer({
  open,
  onOpenChange,
  city,
  countries,
  onSubmit,
  isLoading = false,
  isRTL = false,
}: CityFormDrawerProps) {
  const { t } = useTranslation()
  const isEditing = !!city

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CityFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(cityFormSchema) as any,
    defaultValues: getDefaultValues(city),
  })

  useEffect(() => {
    if (open) {
      reset(getDefaultValues(city))
    }
  }, [open, city?.documentId, reset, city])

  const onFormSubmit = (data: CityFormData) => {
    onSubmit(formDataToSubmitData(data))
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSave = handleSubmit(onFormSubmit as any)

  const headerContent = (
    <div className={cn('flex items-center gap-3')}>
      <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
        <Building2 className="w-6 h-6 text-white" />
      </div>
      <div>
        <h2 className="text-lg font-bold text-white">
          {isEditing ? t('cities.editCity') : t('cities.addCity')}
        </h2>
        <p className="text-sm text-white/80">
          {isEditing
            ? t('cities.editCityDescription')
            : t('cities.addCityDescription')}
        </p>
      </div>
    </div>
  )

  const footerContent = (
    <div className={cn('flex gap-3')}>
      <Button
        type="button"
        variant="outline"
        className="flex-1 h-12 rounded-xl"
        onClick={() => onOpenChange(false)}
        disabled={isLoading}
      >
        <X className={cn('w-4 h-4', isRTL ? 'ms-2' : 'me-2')} />
        {t('common.cancel')}
      </Button>
      <Button
        type="button"
        className="flex-1 h-12 rounded-xl"
        disabled={isLoading}
        onClick={handleSave}
      >
        <Save className={cn('w-4 h-4', isRTL ? 'ms-2' : 'me-2')} />
        {isLoading ? t('common.saving') : t('common.save')}
      </Button>
    </div>
  )

  return (
    <BaseDrawer
      open={open}
      onOpenChange={onOpenChange}
      isRTL={isRTL}
      headerGradient="from-primary to-primary/80"
      headerContent={headerContent}
      footerContent={footerContent}
      maxWidth="sm:max-w-lg"
    >
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      <form id="city-form" onSubmit={handleSubmit(onFormSubmit as any)} className="space-y-6">
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
            {t('cities.basicInformation')}
          </h3>

          <div className="space-y-2">
            <Label htmlFor="code">{t('cities.code')} *</Label>
            <Controller
              name="code"
              control={control}
              render={({ field }) => {
                const { ref: _ref, value, ...fieldProps } = field
                return (
                  <Input
                    id="code"
                    value={value ?? ''}
                    placeholder={t('cities.codePlaceholder')}
                    className={cn('h-11 rounded-xl uppercase', errors.code && 'border-destructive')}
                    dir="ltr"
                    disabled={isEditing}
                    {...fieldProps}
                  />
                )
              }}
            />
            {errors.code && <p className="text-xs text-destructive">{t(errors.code.message || '')}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="nameEn">{t('cities.name')} *</Label>
            <Controller
              name="nameEn"
              control={control}
              render={({ field }) => {
                const { ref: _ref, value, ...fieldProps } = field
                return (
                  <Input
                    id="nameEn"
                    value={value ?? ''}
                    placeholder={t('cities.namePlaceholder')}
                    className={cn('h-11 rounded-xl', errors.nameEn && 'border-destructive')}
                    {...fieldProps}
                  />
                )
              }}
            />
            {errors.nameEn && (
              <p className="text-xs text-destructive">{t(errors.nameEn.message || '')}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="stateProvinceEn">{t('cities.stateProvince')}</Label>
            <Controller
              name="stateProvinceEn"
              control={control}
              render={({ field }) => {
                const { ref: _ref, value, ...fieldProps } = field
                return (
                  <Input
                    id="stateProvinceEn"
                    value={value ?? ''}
                    placeholder={t('cities.stateProvincePlaceholder')}
                    className="h-11 rounded-xl"
                    {...fieldProps}
                  />
                )
              }}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="salesDistrict">{t('cities.salesDistrict')}</Label>
            <Controller
              name="salesDistrict"
              control={control}
              render={({ field }) => {
                const { ref: _ref, value, ...fieldProps } = field
                return (
                  <Input
                    id="salesDistrict"
                    value={value ?? ''}
                    placeholder={t('cities.salesDistrictPlaceholder')}
                    className="h-11 rounded-xl"
                    dir="ltr"
                    {...fieldProps}
                  />
                )
              }}
            />
          </div>

          <div className="flex items-center justify-between py-2">
            <Label htmlFor="isActive" className="cursor-pointer">
              {t('cities.status')}
            </Label>
            <Controller
              name="isActive"
              control={control}
              render={({ field }) => (
                <div className="flex items-center gap-2">
                  <span className={cn('text-sm', !field.value && 'text-muted-foreground')}>
                    {field.value ? t('cities.active') : t('cities.inactive')}
                  </span>
                  <Switch
                    id="isActive"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </div>
              )}
            />
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
            {t('cities.arabicOptional')}
          </h3>

          <div className="space-y-2">
            <Label htmlFor="nameAr">{t('cities.nameArabic')}</Label>
            <Controller
              name="nameAr"
              control={control}
              render={({ field }) => {
                const { ref: _ref, value, ...fieldProps } = field
                return (
                  <Input
                    id="nameAr"
                    value={value ?? ''}
                    placeholder={t('cities.nameArabicPlaceholder')}
                    className="h-11 rounded-xl"
                    dir="rtl"
                    {...fieldProps}
                  />
                )
              }}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="stateProvinceAr">{t('cities.stateProvinceArabic')}</Label>
            <Controller
              name="stateProvinceAr"
              control={control}
              render={({ field }) => {
                const { ref: _ref, value, ...fieldProps } = field
                return (
                  <Input
                    id="stateProvinceAr"
                    value={value ?? ''}
                    placeholder={t('cities.stateProvinceArabicPlaceholder')}
                    className="h-11 rounded-xl"
                    dir="rtl"
                    {...fieldProps}
                  />
                )
              }}
            />
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
            {t('cities.country')}
          </h3>

          <div className="space-y-2">
            <Label>{t('cities.country')}</Label>
            <Controller
              name="countryId"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger className="h-11 rounded-xl">
                    <SelectValue placeholder={t('cities.selectCountry')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_SELECTION_VALUE}>
                      {t('common.none', 'None')}
                    </SelectItem>
                    {countries.length === 0 ? (
                      <div className="py-6 text-center text-sm text-muted-foreground">
                        {t('common.loading')}
                      </div>
                    ) : (
                      countries.map((country) => (
                        <SelectItem key={country.id} value={country.documentId}>
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-xs text-muted-foreground">
                              [{country.code}]
                            </span>
                            <span>{country.name}</span>
                          </div>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              )}
            />
          </div>
        </div>
      </form>
    </BaseDrawer>
  )
}
